import { QueryClient } from '@tanstack/react-query';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

import { Reminder } from '../reminder/_types';
import { normalizeReminder } from '../reminder/normalizers';

import {
  AutomationGroup,
  AutomationLayout,
  ReorderAutomationLayoutPayload,
} from './_types';

const normalizeAutomationGroup = (group: Record<string, unknown>): AutomationGroup => {
  return camelcaseKeys(group, { deep: true }) as AutomationGroup;
};

export const automationGroupsQueryKey = () => ['automation-groups'] as const;

export const automationsQueryKey = (customType?: string) =>
  ['automations', customType] as const;

const reminderIdsMatch = (
  left: Reminder['id'],
  right: Reminder['id']
): boolean => left != null && right != null && String(left) === String(right);

const sortByPosition = <T extends { position?: number }>(items: T[]) =>
  [...items].sort(
    (left, right) => (left.position ?? 0) - (right.position ?? 0)
  );

const sortRemindersByPosition = (items: Reminder[]) =>
  [...items].sort((left, right) => (left.position ?? 0) - (right.position ?? 0));

/** Enabled automations first, then by position (roast default sort). */
export const sortRemindersForDisplay = (items: Reminder[]) =>
  [...items].sort((left, right) => {
    const enabledDiff =
      Number(right.enabled !== false) - Number(left.enabled !== false);
    if (enabledDiff !== 0) {
      return enabledDiff;
    }

    return (left.position ?? 0) - (right.position ?? 0);
  });

export interface AutomationLayoutFilterOptions {
  folderId?: string;
  /** When set, show only automations matching this enabled state. */
  enabledStatus?: 'active' | 'disabled';
  search?: string;
}

export const filterAutomationLayout = (
  layout: AutomationLayout,
  options: AutomationLayoutFilterOptions
): AutomationLayout => {
  const search = options.search?.trim().toLowerCase() ?? '';

  const matchesSearch = (reminder: Reminder) => {
    if (!search) {
      return true;
    }

    return (reminder.name || '').toLowerCase().includes(search);
  };

  const matchesEnabled = (reminder: Reminder) => {
    if (!options.enabledStatus) {
      return true;
    }

    if (options.enabledStatus === 'active') {
      return reminder.enabled !== false;
    }

    return reminder.enabled === false;
  };

  const matchesReminder = (reminder: Reminder) =>
    matchesSearch(reminder) && matchesEnabled(reminder);

  let automationGroups = layout.automationGroups.map((group) => ({
    ...group,
    reminders: sortRemindersForDisplay(
      (group.reminders || []).filter(matchesReminder)
    ),
  }));

  let ungroupedReminders = sortRemindersByPosition(
    layout.ungroupedReminders.filter(matchesReminder)
  );

  let disabledUngroupedReminders = sortRemindersByPosition(
    layout.disabledUngroupedReminders.filter(matchesReminder)
  );

  if (options.folderId === 'ungrouped') {
    automationGroups = [];
  } else if (options.folderId) {
    automationGroups = automationGroups.filter(
      (group) => String(group.id) === String(options.folderId)
    );
    ungroupedReminders = [];
    disabledUngroupedReminders = [];
  }

  return { automationGroups, ungroupedReminders, disabledUngroupedReminders };
};

const reminderIdList = (reminders: Reminder[]) =>
  reminders.map((reminder) => reminder.id!).filter(Boolean);

const containerIdsMatch = (filtered: Identifier[], full: Identifier[]) => {
  if (filtered.length !== full.length) {
    return false;
  }

  const fullSet = new Set(full.map(String));
  return filtered.every((id) => fullSet.has(String(id)));
};

export interface AutomationLayoutReorderOptions {
  /** When false, skip ungrouped container check (e.g. folder filter hides ungrouped). */
  includeUngrouped?: boolean;
}

/** True when each visible container shows the complete reminder set from the full layout. */
export const isAutomationLayoutReorderable = (
  filtered: AutomationLayout,
  full: AutomationLayout,
  options: AutomationLayoutReorderOptions = {}
): boolean => {
  const { includeUngrouped = true } = options;

  const fullGroupsById = new Map(
    full.automationGroups.map((group) => [String(group.id), group])
  );

  for (const group of filtered.automationGroups) {
    const fullGroup = fullGroupsById.get(String(group.id));
    if (!fullGroup) {
      return false;
    }

    if (
      !containerIdsMatch(
        reminderIdList(group.reminders || []),
        reminderIdList(fullGroup.reminders || [])
      )
    ) {
      return false;
    }
  }

  if (includeUngrouped) {
    if (
      !containerIdsMatch(
        reminderIdList(filtered.ungroupedReminders),
        reminderIdList(full.ungroupedReminders)
      )
    ) {
      return false;
    }

    if (
      !containerIdsMatch(
        reminderIdList(filtered.disabledUngroupedReminders),
        reminderIdList(full.disabledUngroupedReminders)
      )
    ) {
      return false;
    }
  }

  return true;
};

/** Places reminders under their automation groups on the client. */
export const buildAutomationLayout = (
  groups: AutomationGroup[],
  reminders: Reminder[]
): AutomationLayout => {
  const sortedGroups = sortByPosition(groups);
  const sortedReminders = sortRemindersForDisplay(reminders);
  const groupIds = new Set(sortedGroups.map((group) => String(group.id)));

  const isUngrouped = (reminder: Reminder) =>
    reminder.automationGroupId == null ||
    !groupIds.has(String(reminder.automationGroupId));

  const ungroupedAll = sortedReminders.filter(isUngrouped);

  const ungroupedReminders = sortRemindersByPosition(
    ungroupedAll.filter((reminder) => reminder.enabled !== false)
  );

  const disabledUngroupedReminders = sortRemindersByPosition(
    ungroupedAll.filter((reminder) => reminder.enabled === false)
  );

  const automationGroups = sortedGroups.map((group) => ({
    ...group,
    reminders: sortedReminders.filter(
      (reminder) =>
        reminder.automationGroupId != null &&
        String(reminder.automationGroupId) === String(group.id)
    ),
  }));

  return { automationGroups, ungroupedReminders, disabledUngroupedReminders };
};

export const patchReminderInAutomationsCache = (
  queryClient: QueryClient,
  savedReminder: Reminder
) => {
  const normalized = normalizeReminder(savedReminder);

  queryClient.setQueriesData<Reminder[]>(
    { queryKey: ['automations'] },
    (current) => {
      if (!current) {
        return current;
      }

      const hasReminder = current.some((reminder) =>
        reminderIdsMatch(reminder.id, savedReminder.id)
      );

      if (hasReminder) {
        return current.map((reminder) =>
          reminderIdsMatch(reminder.id, savedReminder.id)
            ? { ...reminder, ...normalized }
            : reminder
        );
      }

      return [...current, normalized];
    }
  );
};

export const patchRemindersAfterGroupUpdate = (
  queryClient: QueryClient,
  groupId: Identifier,
  reminderIds: Identifier[]
) => {
  const reminderIdSet = new Set(reminderIds.map((id) => String(id)));
  const normalizedGroupId = Number(groupId);

  queryClient.setQueriesData<Reminder[]>(
    { queryKey: ['automations'] },
    (current) => {
      if (!current) {
        return current;
      }

      return current.map((reminder) => {
        const inGroup = String(reminder.automationGroupId) === String(groupId);
        const shouldBeInGroup = reminderIdSet.has(String(reminder.id));

        if (inGroup && !shouldBeInGroup) {
          return { ...reminder, automationGroupId: null };
        }

        if (shouldBeInGroup) {
          return { ...reminder, automationGroupId: normalizedGroupId };
        }

        return reminder;
      });
    }
  );
};

/** Clears automationGroupId on reminders whose group no longer exists. */
export const patchRemindersWithMissingGroups = (
  queryClient: QueryClient,
  groups: AutomationGroup[]
) => {
  const groupIds = new Set(groups.map((group) => String(group.id)));

  queryClient.setQueriesData<Reminder[]>(
    { queryKey: ['automations'] },
    (current) => {
      if (!current) {
        return current;
      }

      let changed = false;
      const next = current.map((reminder) => {
        if (
          reminder.automationGroupId != null &&
          !groupIds.has(String(reminder.automationGroupId))
        ) {
          changed = true;
          return { ...reminder, automationGroupId: null };
        }

        return reminder;
      });

      return changed ? next : current;
    }
  );
};

export const patchRemindersAfterGroupDelete = (
  queryClient: QueryClient,
  groupId: Identifier
) => {
  queryClient.setQueriesData<AutomationGroup[]>(
    { queryKey: automationGroupsQueryKey() },
    (current) =>
      current?.filter((group) => String(group.id) !== String(groupId))
  );

  queryClient.setQueriesData<Reminder[]>(
    { queryKey: ['automations'] },
    (current) => {
      if (!current) {
        return current;
      }

      return current.map((reminder) =>
        String(reminder.automationGroupId) === String(groupId)
          ? { ...reminder, automationGroupId: null }
          : reminder
      );
    }
  );
};

export const fetchAutomationGroups = async (): Promise<AutomationGroup[]> => {
  const response = await api.get('/api/v4/automation_groups');

  const data = camelcaseKeys(response.data, { deep: true }) as {
    automationGroups?: AutomationGroup[];
  };

  return (data.automationGroups || []).map((group) =>
    normalizeAutomationGroup(group as unknown as Record<string, unknown>)
  );
};

export interface CreateAutomationGroupPayload {
  description?: string;
  name: string;
  reminderIds: Identifier[];
}

export const createAutomationGroup = async (
  payload: CreateAutomationGroupPayload
): Promise<AutomationGroup> => {
  const response = await api.post('/api/v4/automation_groups', {
    automation_group: {
      description: payload.description?.trim() || undefined,
      name: payload.name,
      reminder_ids: payload.reminderIds,
    },
  });

  return normalizeAutomationGroup(response.data);
};

export interface UpdateAutomationGroupPayload {
  description?: string;
  name?: string;
  reminderIds?: Identifier[];
}

export const updateAutomationGroup = async (
  id: Identifier,
  payload: UpdateAutomationGroupPayload
): Promise<AutomationGroup> => {
  const response = await api.put(`/api/v4/automation_groups/${id}`, {
    automation_group: {
      description: payload.description?.trim() || undefined,
      name: payload.name,
      reminder_ids: payload.reminderIds,
    },
  });

  return normalizeAutomationGroup(response.data);
};

export const deleteAutomationGroup = async (id: Identifier): Promise<void> => {
  await api.delete(`/api/v4/automation_groups/${id}`);
};

export const reorderAutomationLayout = async (
  payload: ReorderAutomationLayoutPayload
): Promise<void> => {
  await api.put('/api/v4/automation_groups/reorder', {
    groups: payload.groups.map((group) => ({
      id: group.id,
      position: group.position,
      reminder_ids: group.reminderIds,
    })),
    ungrouped_reminder_ids: payload.ungroupedReminderIds,
  });
};

export const flattenAutomationLayout = (layout: AutomationLayout): Reminder[] => {
  const grouped = layout.automationGroups.flatMap((group) => group.reminders || []);
  return [
    ...layout.ungroupedReminders,
    ...layout.disabledUngroupedReminders,
    ...grouped,
  ];
};

type AssignableAutomation = Pick<Reminder, 'id' | 'automationGroupId'>;

const resolveAutomationGroupId = (
  reminder: Reminder,
  formAutomations: AssignableAutomation[] = []
): Reminder['automationGroupId'] => {
  const formMatch = formAutomations.find(
    (automation) =>
      automation.id != null && String(automation.id) === String(reminder.id)
  );

  return formMatch?.automationGroupId ?? reminder.automationGroupId;
};

const isUngroupedReminder = (
  automationGroupId: Reminder['automationGroupId'],
  groupIds: Set<string>
) =>
  automationGroupId == null || !groupIds.has(String(automationGroupId));

/** Ungrouped automations available when creating a folder (ignores page filters). */
export const getCreateFolderSelectableReminders = (
  groups: AutomationGroup[],
  reminders: Reminder[],
  formAutomations: AssignableAutomation[] = []
): Reminder[] => {
  const groupIds = new Set(groups.map((group) => String(group.id)));

  return reminders.filter((reminder) =>
    isUngroupedReminder(
      resolveAutomationGroupId(reminder, formAutomations),
      groupIds
    )
  );
};

/** Ungrouped + current folder automations for the edit-folder dialog (ignores page filters). */
export const getEditFolderSelectableReminders = (
  groups: AutomationGroup[],
  reminders: Reminder[],
  groupId: Identifier,
  formAutomations: AssignableAutomation[] = []
): Reminder[] => {
  const normalizedGroupId = String(groupId);
  const groupIds = new Set(groups.map((group) => String(group.id)));

  const combined = reminders.filter((reminder) => {
    const automationGroupId = resolveAutomationGroupId(reminder, formAutomations);

    return (
      isUngroupedReminder(automationGroupId, groupIds) ||
      (automationGroupId != null &&
        String(automationGroupId) === normalizedGroupId)
    );
  });

  return combined.filter(
    (reminder, index, allReminders) =>
      allReminders.findIndex((item) => String(item.id) === String(reminder.id)) ===
      index
  );
};
